import * as THREE from 'three';

const LN2_4 = 4 * Math.log(2);
const MIN_HALFLIFE = 1e-4;

export interface ScalarSpringStep {
  x: number;
  v: number;
}

export function springStepScalar(
  x: number,
  v: number,
  goal: number,
  halflife: number,
  dt: number,
): ScalarSpringStep {
  if (dt <= 0) return { x, v };
  if (halflife <= 0) return { x: goal, v: 0 };

  const y = (LN2_4 / Math.max(MIN_HALFLIFE, halflife)) / 2;
  const eydt = Math.exp(-y * dt);
  const j0 = x - goal;
  const j1 = v + j0 * y;
  return {
    x: eydt * (j0 + j1 * dt) + goal,
    v: eydt * (v - j1 * y * dt),
  };
}

export function springStepAngle(
  x: number,
  v: number,
  goal: number,
  halflife: number,
  dt: number,
): ScalarSpringStep {
  const unwrappedGoal = x + Math.atan2(Math.sin(goal - x), Math.cos(goal - x));
  return springStepScalar(x, v, unwrappedGoal, halflife, dt);
}

export function springStepVec3(
  x: THREE.Vector3,
  v: THREE.Vector3,
  goal: THREE.Vector3,
  halflife: number,
  dt: number,
): void {
  if (dt <= 0) return;
  if (halflife <= 0) {
    x.copy(goal);
    v.set(0, 0, 0);
    return;
  }

  const sx = springStepScalar(x.x, v.x, goal.x, halflife, dt);
  const sy = springStepScalar(x.y, v.y, goal.y, halflife, dt);
  const sz = springStepScalar(x.z, v.z, goal.z, halflife, dt);
  x.set(sx.x, sy.x, sz.x);
  v.set(sx.v, sy.v, sz.v);
}
